/**
 * Manufacturing Model Configuration
 * COMPLETE extraction from /server/manufacturing/manufacturing-assumptions.js
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const manufacturingConfig: ModelConfig = {
  modelType: 'manufacturing',
  apiEndpoint: '/api/manufacturing',
  displayName: 'Manufacturing',
  resultTabs: standardResultTabs,
  description: 'Production-focused model with capacity, efficiency, and inventory management.',
  icon: '🏭',
  color: '#6366f1',

  assumptionGroups: [
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        { id: 'userEmail', label: 'Your Email', type: 'email', placeholder: 'name@company.com' },
      ],
    },
    {
      id: 'core-production',
      title: '🏭 Core Production Metrics',
      fields: [
        { id: 'productionCapacityMonthly', label: 'Monthly Production Capacity', type: 'number', defaultValue: 2400 },
        { id: 'capacityUtilizationRate', label: 'Utilization Rate (%)', type: 'number', defaultValue: 68 },
        { id: 'avgOrderValue', label: 'Avg Order Value ($)', type: 'number', defaultValue: 1850 },
        { id: 'ordersPerMonth', label: 'Orders per Month', type: 'number', defaultValue: 85 },
      ],
    },
    {
      id: 'capacity-expansion',
      title: '📈 Capacity Expansion',
      fields: [
        { id: 'capacityExpansionY1', label: 'Y1 Expansion (%)', type: 'number', defaultValue: 0 },
        { id: 'capacityExpansionY2', label: 'Y2 Expansion (%)', type: 'number', defaultValue: 25 },
        { id: 'capacityExpansionY3', label: 'Y3 Expansion (%)', type: 'number', defaultValue: 0 },
        { id: 'capacityExpansionY4', label: 'Y4 Expansion (%)', type: 'number', defaultValue: 15 },
        { id: 'capacityExpansionY5', label: 'Y5 Expansion (%)', type: 'number', defaultValue: 0 },
        { id: 'efficiencyImprovementRate', label: 'Efficiency Improvement (%)', type: 'number', defaultValue: 8 },
        { id: 'utilizationImprovementRate', label: 'Utilization Improvement (%)', type: 'number', defaultValue: 5 },
      ],
    },
    {
      id: 'order-growth',
      title: '📊 Order & Demand Growth',
      fields: [
        { id: 'orderGrowthY2', label: 'Y2 Order Growth (%)', type: 'number', defaultValue: 12 },
        { id: 'orderGrowthY3', label: 'Y3 Order Growth (%)', type: 'number', defaultValue: 10 },
        { id: 'orderGrowthY4', label: 'Y4 Order Growth (%)', type: 'number', defaultValue: 8 },
        { id: 'orderGrowthY5', label: 'Y5 Order Growth (%)', type: 'number', defaultValue: 6 },
      ],
    },
    {
      id: 'production-costs',
      title: '⚙️ Raw Materials & Production',
      fields: [
        { id: 'rawMaterialCostPct', label: 'Raw Material Cost (%)', type: 'number', defaultValue: 45 },
        { id: 'directLaborCostPct', label: 'Direct Labor (%)', type: 'number', defaultValue: 22 },
        { id: 'indirectLaborCostPct', label: 'Indirect Labor (%)', type: 'number', defaultValue: 8 },
        { id: 'qualityControlPct', label: 'Quality Control (%)', type: 'number', defaultValue: 3 },
      ],
    },
    {
      id: 'rd',
      title: '🔬 Research & Development',
      fields: [
        { id: 'rdCostsPct', label: 'R&D Costs (%)', type: 'number', defaultValue: 12 },
        { id: 'newProductDevelopmentPct', label: 'New Product Dev (%)', type: 'number', defaultValue: 5 },
        { id: 'processImprovementPct', label: 'Process Improvement (%)', type: 'number', defaultValue: 3 },
      ],
    },
    {
      id: 'facilities',
      title: '🏢 Facilities & Operations',
      fields: [
        { id: 'facilityCostMonth', label: 'Monthly Facility Cost ($)', type: 'number', defaultValue: 28000 },
        { id: 'utilitiesPct', label: 'Utilities (%)', type: 'number', defaultValue: 4.5 },
        { id: 'maintenancePct', label: 'Maintenance (%)', type: 'number', defaultValue: 3 },
        { id: 'equipmentDepreciationPct', label: 'Equipment Depreciation (%)', type: 'number', defaultValue: 8 },
      ],
    },
    {
      id: 'inventory',
      title: '📦 Inventory Management',
      fields: [
        { id: 'inventoryTurnover', label: 'Inventory Turnover', type: 'number', defaultValue: 8 },
        { id: 'rawMaterialInventoryDays', label: 'Raw Material Days', type: 'number', defaultValue: 45 },
        { id: 'wipInventoryDays', label: 'WIP Days', type: 'number', defaultValue: 15 },
        { id: 'finishedGoodsInventoryDays', label: 'Finished Goods Days', type: 'number', defaultValue: 30 },
      ],
    },
    {
      id: 'supply-chain',
      title: '🚚 Supply Chain & Logistics',
      fields: [
        { id: 'shippingCostPct', label: 'Shipping Cost (%)', type: 'number', defaultValue: 3.5 },
        { id: 'supplierManagementCostPct', label: 'Supplier Management (%)', type: 'number', defaultValue: 1.5 },
      ],
    },
    {
      id: 'sales-marketing',
      title: '📈 Sales & Marketing (B2B)',
      fields: [
        { id: 'salesTeamSize', label: 'Sales Team Size', type: 'number', defaultValue: 4 },
        { id: 'avgSalesPersonSalary', label: 'Avg Sales Salary ($)', type: 'number', defaultValue: 85000 },
        { id: 'salesCommissionPct', label: 'Sales Commission (%)', type: 'number', defaultValue: 3 },
        { id: 'marketingPct', label: 'Marketing (%)', type: 'number', defaultValue: 4 },
        { id: 'tradeshowsPct', label: 'Trade Shows (%)', type: 'number', defaultValue: 1.5 },
        { id: 'customerRelationshipPct', label: 'Customer Relations (%)', type: 'number', defaultValue: 2 },
      ],
    },
    {
      id: 'business-ops',
      title: '💼 Business Operations',
      fields: [
        { id: 'productionTeamSize', label: 'Production Team Size', type: 'number', defaultValue: 24 },
        { id: 'adminTeamSize', label: 'Admin Team Size', type: 'number', defaultValue: 8 },
        { id: 'avgProductionWage', label: 'Avg Production Wage ($)', type: 'number', defaultValue: 52000 },
        { id: 'avgAdminSalary', label: 'Avg Admin Salary ($)', type: 'number', defaultValue: 68000 },
        { id: 'techCosts', label: 'ERP/Tech Costs (K)', type: 'number', defaultValue: 12 },
        { id: 'gaPct', label: 'G&A (%)', type: 'number', defaultValue: 14 },
      ],
    },
    {
      id: 'compliance',
      title: '📋 Regulatory & Compliance',
      fields: [
        { id: 'regulatoryCompliancePct', label: 'Regulatory Compliance (%)', type: 'number', defaultValue: 2 },
        { id: 'environmentalCompliancePct', label: 'Environmental Compliance (%)', type: 'number', defaultValue: 1 },
      ],
    },
    {
      id: 'fixed-assets',
      title: '🏗️ Fixed Assets',
      fields: [
        { id: 'initialEquipmentValue', label: 'Equipment Value ($)', type: 'number', defaultValue: 800000 },
        { id: 'initialSoftwareValue', label: 'Software Value ($)', type: 'number', defaultValue: 150000 },
        { id: 'equipmentDepreciationRate', label: 'Equipment Depreciation (%)', type: 'number', defaultValue: 15 },
        { id: 'softwareAmortizationRate', label: 'Software Amortization (%)', type: 'number', defaultValue: 33 },
      ],
    },
    {
      id: 'debt-interest',
      title: '🏦 Debt & Interest',
      fields: [
        { id: 'initialDebtAmount', label: 'Initial Debt ($)', type: 'number', defaultValue: 1200000 },
        { id: 'annualInterestRate', label: 'Interest Rate (%)', type: 'number', defaultValue: 7 },
        { id: 'debtRepaymentYears', label: 'Repayment Years', type: 'number', defaultValue: 10 },
      ],
    },
    {
      id: 'working-capital',
      title: '💰 Working Capital',
      fields: [
        { id: 'daysInAccountsReceivable', label: 'Days in AR', type: 'number', defaultValue: 60 },
        { id: 'daysInAccountsPayable', label: 'Days in AP', type: 'number', defaultValue: 45 },
        { id: 'prepaidExpensesPct', label: 'Prepaid Expenses (%)', type: 'number', defaultValue: 5 },
        { id: 'accruedExpensesPct', label: 'Accrued Expenses (%)', type: 'number', defaultValue: 8 },
        { id: 'minimumCashBalance', label: 'Minimum Cash ($)', type: 'number', defaultValue: 200000 },
      ],
    },
    {
      id: 'capital-raising',
      title: '🚀 Capital Raising',
      fields: [
        { id: 'initialCapitalRaise', label: 'Initial Capital ($)', type: 'number', defaultValue: 2500000 },
        { id: 'year2CapitalRaise', label: 'Year 2 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year3CapitalRaise', label: 'Year 3 Capital ($)', type: 'number', defaultValue: 1500000 },
        { id: 'year4CapitalRaise', label: 'Year 4 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year5CapitalRaise', label: 'Year 5 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'automaticCapitalRaise', label: 'Automatic Capital Raise', type: 'checkbox', defaultValue: true, helpText: 'Automatically raise capital when cash goes negative' },
      ],
    },
    {
      id: 'capex',
      title: '📊 CapEx & Maintenance',
      fields: [
        { id: 'maintenanceCapexPct', label: 'Maintenance CapEx (%)', type: 'number', defaultValue: 2 },
        { id: 'expansionCapexYear2', label: 'Y2 Expansion CapEx ($)', type: 'number', defaultValue: 500000 },
        { id: 'expansionCapexYear4', label: 'Y4 Expansion CapEx ($)', type: 'number', defaultValue: 300000 },
      ],
    },
    {
      id: 'annual-increases',
      title: '📈 Annual Increases',
      fields: [
        { id: 'annualWageIncrease', label: 'Wage Increase (%)', type: 'number', defaultValue: 4 },
        { id: 'annualRentIncrease', label: 'Rent Increase (%)', type: 'number', defaultValue: 3 },
        { id: 'annualUtilityIncrease', label: 'Utility Increase (%)', type: 'number', defaultValue: 5 },
        { id: 'annualMaterialCostIncrease', label: 'Material Cost Increase (%)', type: 'number', defaultValue: 3 },
      ],
    },
    {
      id: 'financial-tax',
      title: '🏛️ Financial & Tax',
      fields: [
        { id: 'corporateTaxRate', label: 'Corporate Tax Rate (%)', type: 'number', defaultValue: 25 },
      ],
    },
  ],

};

export const manufacturingDefaultAssumptions = {
  productionCapacityMonthly: 2400,
  capacityUtilizationRate: 68,
  avgOrderValue: 1850,
  ordersPerMonth: 85,
  capacityExpansionY1: 0,
  capacityExpansionY2: 25,
  capacityExpansionY3: 0,
  capacityExpansionY4: 15,
  capacityExpansionY5: 0,
  efficiencyImprovementRate: 8,
  utilizationImprovementRate: 5,
  orderGrowthY2: 12,
  orderGrowthY3: 10,
  orderGrowthY4: 8,
  orderGrowthY5: 6,
  rawMaterialCostPct: 45,
  directLaborCostPct: 22,
  indirectLaborCostPct: 8,
  qualityControlPct: 3,
  rdCostsPct: 12,
  newProductDevelopmentPct: 5,
  processImprovementPct: 3,
  facilityCostMonth: 28000,
  utilitiesPct: 4.5,
  maintenancePct: 3,
  equipmentDepreciationPct: 8,
  inventoryTurnover: 8,
  rawMaterialInventoryDays: 45,
  wipInventoryDays: 15,
  finishedGoodsInventoryDays: 30,
  shippingCostPct: 3.5,
  supplierManagementCostPct: 1.5,
  salesTeamSize: 4,
  avgSalesPersonSalary: 85000,
  salesCommissionPct: 3,
  marketingPct: 4,
  tradeshowsPct: 1.5,
  customerRelationshipPct: 2,
  productionTeamSize: 24,
  adminTeamSize: 8,
  avgProductionWage: 52000,
  avgAdminSalary: 68000,
  techCosts: 12,
  gaPct: 14,
  regulatoryCompliancePct: 2,
  environmentalCompliancePct: 1,
  corporateTaxRate: 25,
  initialEquipmentValue: 800000,
  initialSoftwareValue: 150000,
  equipmentDepreciationRate: 15,
  softwareAmortizationRate: 33,
  initialDebtAmount: 1200000,
  annualInterestRate: 7,
  debtRepaymentYears: 10,
  daysInAccountsReceivable: 60,
  daysInAccountsPayable: 45,
  prepaidExpensesPct: 5,
  accruedExpensesPct: 8,
  minimumCashBalance: 200000,
  initialCapitalRaise: 2500000,
  year2CapitalRaise: 0,
  year3CapitalRaise: 1500000,
  year4CapitalRaise: 0,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
  maintenanceCapexPct: 2,
  expansionCapexYear2: 500000,
  expansionCapexYear4: 300000,
  annualWageIncrease: 4,
  annualRentIncrease: 3,
  annualUtilityIncrease: 5,
  annualMaterialCostIncrease: 3,
  revenueMultiple: 1.4,
  ebitdaMultiple: 8,
  discountRate: 0.15,
  terminalGrowth: 0.02,
};
